import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { Alert, Badge, Button, Card, Icon, SelectableCard, Skeleton } from '@tb/ui';
import type { BillingPlanDto, CreateOrderResponse } from '@tb/shared';
import { api } from '../api/endpoints';
import { RequestError } from '../api/client';
import { useCustomCode } from '../billing/useCustomCode';
import { Screen } from '../components/Layout';
import { useT, type Translate } from '../i18n/LocaleProvider';
import { useManifestText } from '../i18n/useManifestText';
import { useWalletContext } from '../auth/WalletProvider';
import { notify } from '../telegram/telegram';

type Stage =
  | { name: 'choosing' }
  | { name: 'signing'; plan: BillingPlanDto }
  | { name: 'waiting'; orderId: string }
  | { name: 'paid' }
  | { name: 'error'; message: string };

export function PlansScreen() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useWalletContext();
  const [stage, setStage] = useState<Stage>({ name: 'choosing' });
  const customCode = useCustomCode();
  const t = useT();
  const m = useManifestText();

  const plansQuery = useQuery({ queryKey: ['plans'], queryFn: () => api.billing.plans() });
  const siteQuery = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => api.sites.get(siteId!),
    enabled: Boolean(siteId),
  });

  const waitingId = stage.name === 'waiting' ? stage.orderId : null;
  const orderQuery = useQuery({
    queryKey: ['order', waitingId],
    queryFn: () => api.billing.order(waitingId!),
    enabled: waitingId !== null,
    refetchInterval: 3000,
  });

  useEffect(() => {
    const order = orderQuery.data;
    if (!order || stage.name !== 'waiting') return;

    if (order.status === 'PAID') {
      notify('success');
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['site'] });
      setStage({ name: 'paid' });
      return;
    }

    if (order.status === 'EXPIRED' || order.status === 'CANCELLED') {
      notify('error');
      setStage({ name: 'error', message: t('plans.failed') });
    }
  }, [orderQuery.data, stage.name, queryClient, t]);

  const buy = useMutation({
    mutationFn: (plan: BillingPlanDto) =>
      api.billing.createOrder({
        planCode: plan.code,
        ...(plan.scope === 'SITE' && siteId ? { siteId } : {}),
      }),
    onSuccess: (response, plan) => void sign(response, plan),
    onError: (error: Error) => setStage({ name: 'error', message: error.message }),
  });

  const sign = async (response: CreateOrderResponse, plan: BillingPlanDto) => {
    setStage({ name: 'signing', plan });
    try {
      await tonConnectUI.sendTransaction({
        validUntil: response.transaction.validUntil,
        network: response.transaction.network as never,
        messages: response.transaction.messages.map((message) => ({
          address: message.address,
          amount: message.amount,
          ...(message.payload ? { payload: message.payload } : {}),
        })),
      });

      await api.billing.confirm(response.order.id);
      setStage({ name: 'waiting', orderId: response.order.id });
    } catch (error) {
      const message = error instanceof RequestError ? error.message : t('plans.notSent');
      setStage({ name: 'error', message });
    }
  };

  const back = siteId ? `/sites/${siteId}` : '/';

  if (plansQuery.isPending) {
    return (
      <Screen title={t('plans.title')} back={back}>
        <div className="tb-screen">
          <Skeleton height={90} />
          <Skeleton height={220} />
        </div>
      </Screen>
    );
  }

  if (plansQuery.isError) {
    return (
      <Screen title={t('plans.title')} back={back}>
        <div className="tb-screen">
          <Alert tone="danger" title={t('plans.loadFailed')}>
            {(plansQuery.error as Error).message}
          </Alert>
        </div>
      </Screen>
    );
  }

  const plans = plansQuery.data;
  const addOns = plans.filter((plan) => plan.feature === 'CUSTOM_CODE');
  const sitePlans = plans.filter((plan) => plan.feature !== 'CUSTOM_CODE' && plan.scope === 'SITE');
  const accountPlans = plans.filter(
    (plan) => plan.feature !== 'CUSTOM_CODE' && plan.scope === 'ACCOUNT',
  );
  const busy = buy.isPending || stage.name === 'signing';

  const customCodeState = !customCode.unlocked
    ? null
    : customCode.until === null
      ? t('plans.customCode.forever')
      : t(customCode.fromTrial ? 'plans.customCode.trial' : 'plans.customCode.until', {
          date: customCode.until.slice(0, 10),
        });

  return (
    <Screen title={t('plans.title')} back={back}>
      <div className="tb-screen">
        {stage.name === 'paid' ? (
          <>
            <Alert tone="success" icon="success" title={t('plans.paid.title')}>
              {t('plans.paid.body')}
            </Alert>
            <Button icon="chevron-right" block onClick={() => void navigate(back)}>
              {t('plans.done')}
            </Button>
          </>
        ) : null}

        {stage.name === 'waiting' ? (
          <Card>
            <div className="tb-row">
              <Icon name="spinner" size={20} spin />
              <span className="tb-grow">{t('plans.waiting')}</span>
            </div>
            <span className="tb-hint">{t('plans.waitingHint')}</span>
          </Card>
        ) : null}

        {stage.name === 'error' ? (
          <>
            <Alert tone="danger" title={t('plans.failed')}>
              {stage.message}
            </Alert>
            <Button icon="refresh" block onClick={() => setStage({ name: 'choosing' })}>
              {t('plans.retry')}
            </Button>
          </>
        ) : null}

        {stage.name === 'choosing' || stage.name === 'signing' ? (
          <>
            <Card>
              <span className="tb-label">{t('plans.title')}</span>
              <span className="tb-hint">{t('plans.subtitle')}</span>
              {siteQuery.data ? (
                <div className="tb-row">
                  <Icon name="domain" size={16} />
                  <span className="tb-truncate">
                    {t('plans.forSite', { domain: siteQuery.data.domain })}
                  </span>
                </div>
              ) : null}
            </Card>

            {wallet.status !== 'ready' ? (
              <Alert tone="warning" icon="wallet">
                {t('plans.needWallet')}
              </Alert>
            ) : null}

            {siteId && sitePlans.length > 0 ? (
              <PlanGroup
                title={t('plans.sectionSite')}
                plans={sitePlans}
                disabled={busy || wallet.status !== 'ready'}
                onPick={(plan) => buy.mutate(plan)}
                t={t}
                m={m}
              />
            ) : null}

            {accountPlans.length > 0 ? (
              <PlanGroup
                title={t('plans.sectionAccount')}
                plans={accountPlans}
                disabled={busy || wallet.status !== 'ready'}
                onPick={(plan) => buy.mutate(plan)}
                t={t}
                m={m}
              />
            ) : null}

            {addOns.length > 0 ? (
              <div className="tb-stack">
                <span className="tb-label">{t('plans.sectionCustomCode')}</span>
                <span className="tb-hint">{t('plans.customCode.hint')}</span>
                {customCodeState ? (
                  <Alert tone="success" icon="check">
                    {customCodeState}
                  </Alert>
                ) : null}
                <PlanGroup
                  plans={addOns}
                  disabled={busy || wallet.status !== 'ready'}
                  onPick={(plan) => buy.mutate(plan)}
                  t={t}
                  m={m}
                />
              </div>
            ) : null}

            {plans.length === 0 ? <Alert tone="info">{t('plans.empty')}</Alert> : null}

            {stage.name === 'signing' ? (
              <Card>
                <div className="tb-row">
                  <Icon name="wallet" size={20} />
                  <span className="tb-grow">{t('plans.paying')}</span>
                </div>
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    </Screen>
  );
}

function PlanGroup({
  title,
  plans,
  disabled,
  onPick,
  t,
  m,
}: {
  title?: string;
  plans: BillingPlanDto[];
  disabled: boolean;
  onPick: (plan: BillingPlanDto) => void;
  t: Translate;
  m: ReturnType<typeof useManifestText>;
}) {
  return (
    <div className="tb-stack">
      {title ? <span className="tb-label">{title}</span> : null}
      {plans.map((plan) => (
        <SelectableCard
          key={plan.code}
          icon={plan.period === 'LIFETIME' ? 'star' : 'card'}
          title={m(plan.name)}
          description={
            plan.feature === 'CUSTOM_CODE'
              ? plan.period === 'LIFETIME'
                ? t('plans.forever')
                : t(`plans.period.${plan.period}`)
              : plan.scope === 'ACCOUNT'
                ? t('plans.quota', { count: plan.siteQuota })
                : plan.period === 'LIFETIME'
                  ? t('plans.forever')
                  : t(`plans.period.${plan.period}`)
          }
          disabled={disabled}
          onClick={() => onPick(plan)}
          trailing={
            <Badge tone="accent" icon="gram">
              {plan.priceGram}
            </Badge>
          }
        />
      ))}
    </div>
  );
}
